import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useProviderSignInMutation, useSigninMutation } from 'src/context/api/auth';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { RouterLink } from 'src/routes/components';
import { useBoolean } from 'src/hooks/use-boolean';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useRouter } from 'src/routes/hooks';
import { useLocales } from 'src/locales';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Grid } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { enqueueSnackbar } from 'notistack';
import mixpanel from "mixpanel-browser";
import { useAuth } from 'src/auth';
import SSOProviderLoader from './sso-provider-loader';
import { Helmet } from 'react-helmet-async';
import { SplashScreen } from 'src/components/loading-screen';
import { SigninQueryArg } from 'src/context/api/auth/query';

const PENDING_PROVIDER_KEY = 'pendingProviderSignIn';

// Every case must navigate: staying on /auth/signin once the user is loaded lands on the
// router's catch-all (404), e.g. for an admin who belongs to no workspace.
const getPostSigninPath = (user: { role?: string; workspaces?: { id: number }[] }) => {
  const workspaces = user.workspaces || [];
  if (user.role !== "admin" && workspaces.length === 1) {
    return `/brand/search-performance/overview?workspace=${workspaces[0].id}`;
  }
  return "/";
};

export default function Signin() {
  const router = useRouter();
  // Start in loading state when coming back from the Auth0 redirect, so the form never flashes.
  const [providerLoading, setProviderLoading] = useState(() => !!sessionStorage.getItem(PENDING_PROVIDER_KEY));
  const [signin] = useSigninMutation();
  const password = useBoolean();
  const { reload } = useAuth();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { t } = useLocales();
  const signinSchema = Yup.object().shape({
    email: Yup.string().required(t('fieldRequired', { field: "email" })),
    password: Yup.string().required(t('fieldRequired', { field: t("auth.form.signin.password") }))
  });
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams()
  const [providerSignIn,] = useProviderSignInMutation();
  const { loginWithRedirect, isAuthenticated, isLoading: isAuth0Loading, error: auth0Error, getAccessTokenSilently } = useAuth0();
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);

  const signinWrapper = async (args: SigninQueryArg) => {
    signin(args).unwrap().then((user) => {
      if (user) {
        mixpanel.identify(String(user.id));

        mixpanel.people.set({
          $email: user.email,
          $name: `${user.profile.firstname} ${user.profile.lastname}`,
          role: user.role,
        });

        reload();
        router.replace(getPostSigninPath(user));
      }
    }).catch((error) => {
      const reason = error?.data.message || error?.data.reason || 'UnknownError';
      setErrorMsg(t('auth.' + reason));
    });
  }

  const { error, reason } = useMemo(() => {
    if (searchParams.has("error")) {
      return { error: searchParams.get("error"), reason: searchParams.has("reason") ? searchParams.get("reason") : null }
    }
    return { error: null, reason: null }
  }, [searchParams])

  const methods = useForm({
    resolver: yupResolver(signinSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });


  // Full-page redirect rather than loginWithPopup: Safari drops window.opener once the
  // popup has gone through Google, so Auth0 can't post the result back and stays blank.
  // The pending provider survives the round-trip in sessionStorage.
  const signInWithProvider = async (provider: any) => {
    setProviderLoading(true);
    sessionStorage.setItem(PENDING_PROVIDER_KEY, provider);
    try {
      await loginWithRedirect({
        appState: { returnTo: '/auth/signin' },
        authorizationParams: { connection: provider },
      });
    } catch {
      sessionStorage.removeItem(PENDING_PROVIDER_KEY);
      setProviderLoading(false);
      enqueueSnackbar('Authentification Error', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      });
    }
  }

  const completeProviderSignIn = async (provider: string) => {
    setProviderLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response: any = await providerSignIn({ token, provider });

      if (("error" in response) && response.error.data.reason) {
        enqueueSnackbar(t(`auth.${response.error.data.reason}`), { variant: 'error' });
        setProviderLoading(false);
        return;
      }
      else if (("error" in response) && !response.error.data.reason) {
        throw new Error('Unknown error');
      }

      if (response.data.user) {
        const user = response.data.user;

        mixpanel.identify(String(user.id));

        mixpanel.people.set({
          $email: user.email,
          $name: `${user.profile.firstname} ${user.profile.lastname}`,
          role: user.role,
        });

        reload()
        router.replace(getPostSigninPath(user));
      }

      else {
        enqueueSnackbar('Error: User login failed', { variant: 'error' });
        setProviderLoading(false);
      }

    } catch (error) {
      enqueueSnackbar('Authentification Error', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      });
      setProviderLoading(false);
      // No Auth0 user cleanup here: with the popup flow the stale `isAuthenticated` closure kept it
      // from ever running; with the redirect flow it would delete the account on any transient error.
    }
    // No reset on success: keep the loader until the navigation replaces this page.
  }

  useEffect(() => {
    if (isAuth0Loading) return;
    const provider = sessionStorage.getItem(PENDING_PROVIDER_KEY);
    if (!provider) return;
    sessionStorage.removeItem(PENDING_PROVIDER_KEY);

    if (auth0Error || !isAuthenticated) {
      setProviderLoading(false);
      enqueueSnackbar('Authentification Error', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      });
      return;
    }
    completeProviderSignIn(provider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth0Loading, isAuthenticated, auth0Error]);

  useEffect(() => {
    if (error && reason) {
      enqueueSnackbar(t(`auth.form.signin.error.${error}.${reason}`), { variant: 'error' });
    }
  }, [error, reason, t]);

  const signInWithSaml = () => {
    router.push(`/auth/eidp`);
  }

  if (isLoadingSSO || providerLoading) {
    return <SplashScreen />
  }

  return (
    <>
      <Helmet>
        <title>{t('sign_in_to_your_account')}</title>
      </Helmet>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'rgba(25, 25, 25, 1)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '1597.269px',
            height: '1597.269px',
            left: '-593.914px',
            top: '-915.315px',
            borderRadius: '1597.269px',
            opacity: 0.12,
            background:
              'radial-gradient(50.5% 50.5% at 51.37% 50.48%, #F1FFAA 6.57%, #A682F7 100%)',
            filter: 'blur(250px)',
            transform: 'rotate(-19.212deg)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Grid
          xs={12}
          md={6}
          sx={{
            display: {
              xs: 'none',
              md: 'flex',
            },
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 4, md: 10 },
            color: 'white',
          }}
        >
          <Box>

            <Box sx={{ mb: '32px' }}>
              <svg width="360" height="114" viewBox="0 0 360 114" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M80.6572 23.5703H97.2841C105.7 23.5703 110.983 27.9099 110.983 35.4919C110.983 43.074 105.703 47.4135 97.2841 47.4135H87.5593V60.1695H80.6572V23.5703ZM97.0727 41.2428C101.727 41.2428 104.078 39.0459 104.078 35.4919C104.078 31.9379 101.724 29.741 97.0727 29.741H87.5566V41.2428H97.0727Z" fill="white" />
                <path d="M133.836 34.2351H140.424V60.1669H133.836V34.2351Z" fill="white" />
                <path d="M145.584 34.2353H152.173V37.6864C154.003 34.8095 156.771 33.5039 159.7 33.5039C163.308 33.5039 165.714 34.9667 166.914 37.9492C168.745 35.0209 171.828 33.5039 175.175 33.5039C180.455 33.5039 183.54 36.7979 183.54 42.0259V60.167H176.953V44.1172C176.953 40.8747 175.593 39.2548 172.875 39.2548C169.896 39.2548 167.856 41.5545 167.856 45.737V60.167H161.269V44.22C161.269 40.9262 159.856 39.252 157.086 39.252C154.315 39.252 152.173 41.6575 152.173 45.6314V60.167H145.584V34.2353Z" fill="white" />
                <path d="M201.363 33.5039C208.734 33.5039 214.016 38.889 214.016 46.4712V48.7709H194.044C194.461 52.7448 197.233 55.2532 201.154 55.2532C204.554 55.2532 206.696 53.6848 208.316 50.4965L213.23 53.319C210.668 58.5472 206.748 60.9012 201.049 60.9012C193.886 61.0583 187.559 55.1503 187.716 47.2025C187.716 39.0977 193.833 33.5039 201.363 33.5039ZM207.899 43.7514C207.271 40.4575 204.763 38.5234 201.154 38.5234C197.809 38.5234 195.141 40.666 194.149 43.7514H207.899Z" fill="white" />
                <path d="M218.455 23.5703H225.043V60.1695H218.455V23.5703Z" fill="white" />
                <path d="M230.721 34.2351H237.309V60.1669H230.721V34.2351Z" fill="white" />
                <path d="M254.665 44.274C250.534 43.437 248.547 42.2885 248.547 40.6144C248.547 39.0974 250.167 38.3146 252.47 38.3146C255.116 38.3146 257.197 39.9642 258.3 42.8004L263.084 40.0374C260.941 35.4892 257.855 33.501 252.365 33.501C247.187 33.501 242.691 36.2721 242.691 41.0289C242.691 45.474 246.245 48.0881 251.736 49.1336C255.659 49.9165 257.279 50.8077 257.279 52.4791C257.279 54.3076 255.344 55.3559 253.201 55.3559C250.089 55.3559 247.827 53.262 246.602 50.0059L241.229 53.1075C243.162 57.6557 247.085 60.8981 253.096 60.8981C259.421 60.8981 263.344 57.1871 263.344 52.2705C263.344 47.931 260.73 45.5255 254.665 44.2714V44.274Z" fill="white" />
                <path d="M234.018 31.4151C236.185 31.4151 237.94 29.659 237.94 27.4928C237.94 25.3264 236.185 23.5703 234.018 23.5703C231.851 23.5703 230.096 25.3264 230.096 27.4928C230.096 29.659 231.851 31.4151 234.018 31.4151Z" fill="white" />
                <path d="M137.129 31.4151C139.295 31.4151 141.051 29.659 141.051 27.4928C141.051 25.3264 139.295 23.5703 137.129 23.5703C134.962 23.5703 133.206 25.3264 133.206 27.4928C133.206 29.659 134.962 31.4151 137.129 31.4151Z" fill="white" />
                <path d="M114.744 34.2353H121.332V38.3663C122.849 35.2809 125.255 33.5039 128.546 33.5039C129.329 33.5039 130.114 33.6095 130.794 33.8182L129.748 40.0404C129.069 39.7775 128.389 39.6747 127.606 39.6747C123.789 39.6747 121.332 42.9172 121.332 47.3597V60.1698H114.744V34.238V34.2353Z" fill="white" />
                <path d="M49.5928 48.1528L67.677 50.878L57.8494 54.7353L56.2755 65.1752L49.5955 48.1528H49.5928Z" fill="white" />
                <path d="M51.2529 40.8716L64.6591 28.4326L61.5466 38.523L68.7277 46.2622L51.2529 40.8716Z" fill="white" />
                <path d="M46.6004 35.0338L45.2324 16.7979L51.181 25.5203L61.7103 24.7321L46.6004 35.0338Z" fill="white" />
                <path d="M39.1314 35.0338L24.0215 24.7321L34.5507 25.5203L40.4966 16.7979L39.1314 35.0338Z" fill="white" />
                <path d="M34.4747 40.8718L17 46.2624L24.1811 38.5233L21.0687 28.4355L34.4747 40.8745V40.8718Z" fill="white" />
                <path d="M36.1411 48.1528L29.4611 65.1752L27.8872 54.7353L18.0596 50.878L36.1411 48.1528Z" fill="white" />
                <path d="M42.866 51.3926L52.0084 67.2286L42.866 61.949L33.7236 67.2286L42.866 51.3926Z" fill="white" />
                <path d="M277.669 60.74C277.185 60.74 276.641 60.6917 276.038 60.5951C275.458 60.4983 274.901 60.3776 274.37 60.2326C273.863 60.0876 273.464 59.9305 273.174 59.7613C273.005 59.6647 272.871 59.568 272.775 59.4713C272.703 59.3506 272.666 59.1572 272.666 58.8913L272.485 53.2005C272.485 52.7172 272.643 52.4756 272.956 52.4756C273.223 52.4756 273.404 52.6931 273.5 53.1281L273.826 54.5055C274.648 57.9611 276.183 59.6888 278.43 59.6888C279.638 59.6888 280.593 59.2901 281.294 58.4927C282.019 57.6711 282.381 56.5232 282.381 55.0491C282.381 53.6235 282.006 52.2943 281.258 51.062C280.533 49.8295 279.36 48.6092 277.741 47.401C275.953 46.1202 274.648 44.8877 273.826 43.7037C273.005 42.4953 272.594 41.2147 272.594 39.8615C272.594 38.0732 273.21 36.6475 274.443 35.5842C275.699 34.4967 277.294 33.9531 279.228 33.9531C279.953 33.9531 280.653 34.0376 281.33 34.2068C282.006 34.376 282.563 34.6176 282.998 34.9317C283.143 35.0283 283.251 35.1371 283.324 35.258C283.42 35.3788 283.469 35.5358 283.469 35.7292L283.578 41.2026C283.578 41.6133 283.42 41.8188 283.106 41.8188C282.889 41.8188 282.731 41.6496 282.635 41.3113L282.381 40.4051C281.849 38.5445 281.294 37.1912 280.714 36.3453C280.158 35.4755 279.348 35.0405 278.285 35.0405C277.246 35.0405 276.413 35.3788 275.784 36.0555C275.18 36.7321 274.878 37.6866 274.878 38.919C274.878 39.9581 275.216 40.9488 275.893 41.8912C276.569 42.8337 277.754 43.9816 279.445 45.3348C281.258 46.8088 282.574 48.2346 283.396 49.612C284.241 50.9652 284.664 52.4152 284.664 53.9617C284.664 55.315 284.363 56.4991 283.759 57.514C283.154 58.529 282.32 59.3263 281.258 59.9063C280.218 60.4621 279.021 60.74 277.669 60.74ZM289.893 38.484C289.385 38.484 288.95 38.3027 288.588 37.9403C288.249 37.5778 288.08 37.1187 288.08 36.5628C288.08 36.0071 288.249 35.5601 288.588 35.2217C288.95 34.8592 289.385 34.678 289.893 34.678C290.4 34.678 290.823 34.8592 291.161 35.2217C291.499 35.5601 291.669 36.0071 291.669 36.5628C291.669 37.1187 291.499 37.5778 291.161 37.9403C290.823 38.3027 290.4 38.484 289.893 38.484ZM286.848 60.4138C286.485 60.4138 286.304 60.2808 286.304 60.0151C286.304 59.7976 286.461 59.6526 286.775 59.5801L287.21 59.5076C287.814 59.411 288.201 59.2659 288.37 59.0726C288.539 58.8551 288.624 58.4927 288.624 57.9852V45.806C288.624 45.3711 288.551 45.0811 288.406 44.9361C288.285 44.767 288.056 44.6582 287.718 44.6098L286.956 44.5011C286.643 44.477 286.485 44.3441 286.485 44.1023C286.485 43.9091 286.679 43.7761 287.065 43.7037C287.814 43.5828 288.419 43.3895 288.878 43.1237C289.336 42.8578 289.784 42.5316 290.219 42.145C290.436 41.9275 290.618 41.8188 290.763 41.8188C290.98 41.8188 291.089 41.9637 291.089 42.2537V57.9852C291.089 58.4927 291.161 58.8551 291.306 59.0726C291.451 59.2901 291.729 59.423 292.14 59.4713L293.046 59.5801C293.311 59.6285 293.445 59.7613 293.445 59.9788C293.445 60.2688 293.264 60.4138 292.901 60.4138H286.848ZM300.283 68.207C298.446 68.207 296.996 67.8325 295.933 67.0833C294.87 66.3583 294.339 65.4763 294.339 64.4372C294.339 64.0023 294.399 63.6036 294.52 63.2411C294.64 62.9028 294.894 62.5041 295.281 62.045C295.691 61.5858 296.308 60.9937 297.129 60.2688C297.419 60.003 297.564 59.7613 297.564 59.5438C297.564 59.3022 297.419 59.121 297.129 59.0001C296.259 58.6377 295.74 58.1785 295.571 57.6227C295.401 57.0427 295.608 56.5111 296.186 56.0278L298.796 53.7805C299.086 53.5388 299.075 53.3335 298.76 53.1642C297.77 52.681 296.984 51.9318 296.404 50.917C295.824 49.902 295.535 48.8146 295.535 47.6546C295.535 46.5431 295.776 45.5402 296.259 44.6461C296.743 43.752 297.395 43.0391 298.216 42.5075C299.039 41.9758 299.956 41.7101 300.971 41.7101C301.551 41.7101 302.108 41.8067 302.639 42C302.978 42.1208 303.183 42.0121 303.255 41.6738C303.473 40.7555 303.871 40.0426 304.451 39.5352C305.055 39.0036 305.648 38.7377 306.228 38.7377C306.784 38.7377 307.23 38.9068 307.569 39.2452C307.931 39.5593 308.113 39.9581 308.113 40.4413C308.113 40.8522 307.991 41.1785 307.75 41.4201C307.509 41.6617 307.206 41.7826 306.844 41.7826C306.53 41.7826 306.3 41.7221 306.155 41.6013C306.01 41.4805 305.878 41.3596 305.756 41.2388C305.635 41.118 305.443 41.0576 305.176 41.0576C304.596 41.0576 304.234 41.3717 304.089 42C304.016 42.2417 304.016 42.4471 304.089 42.6162C304.185 42.7853 304.319 42.9546 304.488 43.1237C305.068 43.6795 305.515 44.3561 305.829 45.1536C306.168 45.951 306.336 46.8331 306.336 47.7996C306.336 48.9596 306.095 49.9987 305.611 50.917C305.153 51.8352 304.511 52.5601 303.69 53.0918C302.893 53.6235 301.986 53.8892 300.971 53.8892C300.536 53.8892 300.163 53.9858 299.848 54.1792C299.534 54.3483 299.22 54.6021 298.905 54.9405L298.289 55.6291C297.975 56.0157 297.879 56.3903 297.999 56.7528C298.144 57.0911 298.483 57.2602 299.014 57.2602H302.131C303.871 57.2602 305.249 57.6952 306.264 58.5652C307.279 59.411 307.786 60.583 307.786 62.0812C307.786 63.2652 307.448 64.3165 306.771 65.2347C306.095 66.153 305.189 66.878 304.053 67.4096C302.941 67.9412 301.685 68.207 300.283 68.207ZM300.935 52.9831C301.805 52.9831 302.494 52.5118 303.001 51.5693C303.509 50.627 303.763 49.3462 303.763 47.7271C303.763 46.1565 303.496 44.8998 302.965 43.9573C302.458 42.9908 301.781 42.5075 300.935 42.5075C300.09 42.5075 299.401 42.9908 298.869 43.9573C298.361 44.8998 298.108 46.1565 298.108 47.7271C298.108 49.3462 298.361 50.627 298.869 51.5693C299.376 52.5118 300.065 52.9831 300.935 52.9831ZM300.718 67.4096C301.588 67.4096 302.385 67.2162 303.11 66.8296C303.835 66.443 304.403 65.8992 304.814 65.1985C305.249 64.5218 305.466 63.7486 305.466 62.8786C305.466 61.7912 305.14 60.9575 304.488 60.3776C303.859 59.7735 302.965 59.4713 301.805 59.4713H300.971C300.223 59.4713 299.655 59.5438 299.268 59.6888C298.905 59.8338 298.555 60.0876 298.216 60.4501C297.468 61.2475 296.996 61.8878 296.803 62.3712C296.61 62.8545 296.513 63.3498 296.513 63.8573C296.513 64.9206 296.875 65.7785 297.6 66.4308C298.35 67.0833 299.389 67.4096 300.718 67.4096ZM309.503 60.4138C309.14 60.4138 308.959 60.2808 308.959 60.0151C308.959 59.7976 309.115 59.6526 309.43 59.5801L309.865 59.5076C310.373 59.411 310.699 59.2659 310.844 59.0726C311.013 58.8551 311.098 58.4927 311.098 57.9852V45.806C311.098 45.3711 311.025 45.0811 310.88 44.9361C310.759 44.767 310.529 44.6582 310.191 44.6098L309.43 44.5011C309.115 44.477 308.959 44.3441 308.959 44.1023C308.959 43.9091 309.151 43.7761 309.539 43.7037C310.288 43.5828 310.855 43.3895 311.243 43.1237C311.653 42.8578 312.076 42.5316 312.511 42.145C312.729 41.9275 312.91 41.8188 313.055 41.8188C313.273 41.8188 313.381 41.9637 313.381 42.2537V43.8848C313.381 44.1023 313.454 44.2473 313.599 44.3198C313.768 44.3682 313.949 44.2957 314.141 44.1023C315.109 43.1841 315.954 42.5558 316.679 42.2175C317.429 41.8792 318.214 41.7101 319.035 41.7101C320.123 41.7101 320.993 42.0967 321.645 42.87C322.298 43.6191 322.624 44.7307 322.624 46.2047V57.9852C322.624 58.4927 322.696 58.8551 322.841 59.0726C323.01 59.2659 323.349 59.3988 323.856 59.4713L324.654 59.5801C324.92 59.6285 325.053 59.7735 325.053 60.0151C325.053 60.2808 324.908 60.4138 324.618 60.4138H318.528C318.19 60.4138 318.02 60.2808 318.02 60.0151C318.02 59.7735 318.154 59.6285 318.419 59.5801L318.926 59.5076C319.434 59.4351 319.76 59.2901 319.905 59.0726C320.075 58.8551 320.159 58.4927 320.159 57.9852V46.6035C320.159 45.4193 319.941 44.5857 319.506 44.1023C319.071 43.6191 318.455 43.3775 317.658 43.3775C316.885 43.3775 316.184 43.607 315.555 44.0661C314.951 44.5011 314.468 45.0931 314.105 45.8422C313.744 46.5672 313.563 47.3767 313.563 48.2708V57.9852C313.563 58.4927 313.635 58.8551 313.78 59.0726C313.949 59.2901 314.286 59.423 314.794 59.4713L315.773 59.5801C316.039 59.6285 316.171 59.7613 316.171 59.9788C316.171 60.2688 315.99 60.4138 315.628 60.4138H309.503ZM329.791 60.74C328.776 60.74 327.943 60.4258 327.29 59.7976C326.638 59.1451 326.311 58.2872 326.311 57.224C326.311 56.185 326.638 55.2062 327.29 54.288C327.943 53.3455 328.836 52.4997 329.973 51.7506C331.108 51.0015 332.401 50.3973 333.851 49.9382C334.165 49.8416 334.321 49.6362 334.321 49.3221V46.0235C334.321 44.8636 334.14 44.042 333.779 43.5587C333.416 43.0512 332.92 42.7975 332.293 42.7975C331.688 42.7975 331.133 43.0753 330.625 43.6312C330.141 44.1628 329.791 45.069 329.574 46.3497C329.453 47.123 329.186 47.6908 328.776 48.0533C328.389 48.3917 327.99 48.5608 327.58 48.5608C326.904 48.5608 326.565 48.2225 326.565 47.5458C326.565 46.8451 326.759 46.1565 327.145 45.4798C327.556 44.779 328.088 44.1507 328.74 43.595C329.393 43.015 330.105 42.5558 330.879 42.2175C331.651 41.8792 332.425 41.7101 333.199 41.7101C335.59 41.7101 336.786 43.0996 336.786 45.8785V56.9702C336.786 57.9852 337.04 58.4927 337.548 58.4927C337.863 58.4927 338.153 58.2872 338.418 57.8765C338.684 57.4415 338.816 56.7166 338.816 55.7016C338.816 55.3149 338.974 55.1216 339.288 55.1216C339.578 55.1216 339.723 55.3271 339.723 55.7378C339.723 57.502 339.409 58.7826 338.78 59.5801C338.176 60.3533 337.488 60.74 336.714 60.74C336.134 60.74 335.675 60.5105 335.336 60.0513C334.999 59.568 334.769 59.0001 334.648 58.3477C334.624 58.1061 334.515 57.9852 334.321 57.9852C334.153 57.9611 333.984 58.0698 333.815 58.3115C333.235 59.1088 332.643 59.7129 332.039 60.1238C331.434 60.5346 330.685 60.74 329.791 60.74ZM330.77 59.0726C331.398 59.0726 331.978 58.8551 332.51 58.4202C333.041 57.9611 333.476 57.357 333.815 56.6078C334.153 55.8587 334.321 55.0128 334.321 54.0705V51.4243C334.321 50.9652 334.068 50.8323 333.561 51.0257C332.086 51.5573 330.939 52.2943 330.118 53.2367C329.32 54.1551 328.921 55.2666 328.921 56.5716C328.921 58.239 329.538 59.0726 330.77 59.0726ZM340.644 60.4138C340.354 60.4138 340.209 60.2808 340.209 60.0151C340.209 59.7735 340.343 59.6285 340.608 59.5801L341.115 59.5076C341.623 59.4351 341.998 59.2901 342.239 59.0726C342.48 58.8551 342.601 58.4927 342.601 57.9852V37.2153C342.601 36.7803 342.529 36.4783 342.384 36.3091C342.263 36.14 342.034 36.0433 341.695 36.0192L340.861 35.9105C340.595 35.8621 340.463 35.7171 340.463 35.4755C340.463 35.2338 340.595 35.0888 340.861 35.0405C341.538 34.8955 342.118 34.7263 342.601 34.533C343.085 34.3397 343.484 34.1463 343.798 33.9531C344.184 33.7115 344.474 33.5906 344.668 33.5906C344.934 33.5906 345.066 33.7838 345.066 34.1706V57.9852C345.066 58.4927 345.139 58.8551 345.284 59.0726C345.453 59.2659 345.791 59.3988 346.299 59.4713L347.133 59.5801C347.398 59.6285 347.531 59.7735 347.531 60.0151C347.531 60.2808 347.386 60.4138 347.096 60.4138H340.644Z" fill="white" />
                <path d="M160.44 95V83.36C160.44 82.4267 160.707 81.7 161.24 81.18C161.787 80.66 162.613 80.4 163.72 80.4C163.987 80.4 164.193 80.4133 164.34 80.44C164.5 80.4667 164.633 80.5 164.74 80.54V81.8C164.647 81.7733 164.513 81.7467 164.34 81.72C164.18 81.6933 164.007 81.68 163.82 81.68C163.207 81.68 162.753 81.8333 162.46 82.14C162.18 82.4467 162.04 82.88 162.04 83.44V95H160.44ZM158.34 86.16V84.8H164.52V86.16H158.34ZM170.52 95.2C169.52 95.2 168.64 94.9667 167.88 94.5C167.12 94.0333 166.526 93.4 166.1 92.6C165.686 91.7867 165.48 90.8733 165.48 89.86C165.48 88.8333 165.693 87.9267 166.12 87.14C166.546 86.3533 167.133 85.7333 167.88 85.28C168.64 84.8267 169.52 84.6 170.52 84.6C171.533 84.6 172.413 84.8267 173.16 85.28C173.92 85.7333 174.506 86.3533 174.92 87.14C175.346 87.9267 175.56 88.8333 175.56 89.86C175.56 90.8733 175.346 91.7867 174.92 92.6C174.506 93.4 173.92 94.0333 173.16 94.5C172.413 94.9667 171.533 95.2 170.52 95.2ZM170.52 93.84C171.213 93.84 171.813 93.6733 172.32 93.34C172.826 92.9933 173.22 92.52 173.5 91.92C173.78 91.32 173.92 90.6267 173.92 89.84C173.92 88.6667 173.606 87.7267 172.98 87.02C172.366 86.3133 171.546 85.96 170.52 85.96C169.506 85.96 168.686 86.3133 168.06 87.02C167.433 87.7267 167.12 88.6667 167.12 89.84C167.12 90.6267 167.26 91.32 167.54 91.92C167.82 92.52 168.213 92.9933 168.72 93.34C169.24 93.6733 169.84 93.84 170.52 93.84ZM177.959 95V84.8H179.459V87.4H179.559V95H177.959ZM179.559 89.68L179.299 87.26C179.539 86.3933 179.966 85.7333 180.579 85.28C181.193 84.8267 181.899 84.6 182.699 84.6C183.033 84.6 183.259 84.6267 183.379 84.68V86.22C183.313 86.1933 183.219 86.18 183.099 86.18C182.979 86.1667 182.833 86.16 182.659 86.16C181.646 86.16 180.873 86.4667 180.339 87.08C179.819 87.68 179.559 88.5467 179.559 89.68ZM185.459 95V84.8H186.959V87.2H187.059V95H185.459ZM192.339 95V88.24C192.339 87.4667 192.166 86.9 191.819 86.54C191.473 86.18 190.973 86 190.319 86C189.706 86 189.153 86.1467 188.659 86.44C188.166 86.7333 187.773 87.14 187.479 87.66C187.199 88.18 187.059 88.7867 187.059 89.48L186.799 87.06C187.146 86.3 187.666 85.7 188.359 85.26C189.053 84.82 189.806 84.6 190.619 84.6C191.606 84.6 192.406 84.8933 193.019 85.48C193.633 86.0533 193.939 86.8333 193.939 87.82V95H192.339ZM199.219 95V88.24C199.219 87.4667 199.046 86.9 198.699 86.54C198.353 86.18 197.853 86 197.199 86C196.586 86 196.033 86.1467 195.539 86.44C195.046 86.7333 194.653 87.14 194.359 87.66C194.079 88.18 193.939 88.7867 193.939 89.48L193.539 87.06C193.886 86.3 194.419 85.7 195.139 85.26C195.859 84.82 196.633 84.6 197.459 84.6C198.446 84.6 199.253 84.8933 199.879 85.48C200.506 86.0533 200.819 86.8333 200.819 87.82V95H199.219ZM208.077 95.2C207.104 95.2 206.244 94.9733 205.497 94.52C204.75 94.0533 204.164 93.42 203.737 92.62C203.31 91.82 203.097 90.9 203.097 89.86C203.097 88.82 203.304 87.9067 203.717 87.12C204.144 86.32 204.737 85.7 205.497 85.26C206.257 84.82 207.13 84.6 208.117 84.6C209.064 84.6 209.884 84.8067 210.577 85.22C211.284 85.6333 211.824 86.2133 212.197 86.96C212.584 87.7067 212.777 88.5867 212.777 89.6C212.777 89.7333 212.77 89.86 212.757 89.98C212.757 90.1 212.744 90.2333 212.717 90.38H204.297V89.04H211.737L211.217 89.78C211.217 88.5533 210.944 87.6133 210.397 86.96C209.864 86.2933 209.104 85.96 208.117 85.96C207.104 85.96 206.284 86.3133 205.657 87.02C205.044 87.7133 204.737 88.6533 204.737 89.84C204.737 91.0533 205.044 92.0267 205.657 92.76C206.284 93.48 207.117 93.84 208.157 93.84C208.81 93.84 209.384 93.7 209.877 93.42C210.37 93.1267 210.73 92.7133 210.957 92.18H212.477C212.144 93.1133 211.584 93.8533 210.797 94.4C210.024 94.9333 209.117 95.2 208.077 95.2ZM215.186 95V84.8H216.686V87.4H216.786V95H215.186ZM216.786 89.68L216.526 87.26C216.766 86.3933 217.193 85.7333 217.806 85.28C218.419 84.8267 219.126 84.6 219.926 84.6C220.259 84.6 220.486 84.6267 220.606 84.68V86.22C220.539 86.1933 220.446 86.18 220.326 86.18C220.206 86.1667 220.059 86.16 219.886 86.16C218.873 86.16 218.099 86.4667 217.566 87.08C217.046 87.68 216.786 88.5467 216.786 89.68ZM222.686 95V80.6H224.286V95H222.686ZM228.371 99.1L230.651 93.8L231.011 93.24L234.351 84.8H235.991L230.071 99.1H228.371ZM230.831 95.1L226.251 84.8H228.011L231.951 94.02L230.831 95.1ZM248.183 95.2C247.197 95.2 246.283 95.02 245.443 94.66C244.617 94.2867 243.897 93.7733 243.283 93.12C242.67 92.4533 242.197 91.6733 241.863 90.78C241.53 89.8733 241.363 88.88 241.363 87.8C241.363 86.72 241.53 85.7333 241.863 84.84C242.197 83.9333 242.663 83.1533 243.263 82.5C243.877 81.8333 244.597 81.32 245.423 80.96C246.263 80.5867 247.177 80.4 248.163 80.4C249.243 80.4 250.21 80.62 251.063 81.06C251.93 81.5 252.637 82.12 253.183 82.92C253.743 83.72 254.09 84.6533 254.223 85.72H252.583C252.423 84.5333 251.95 83.6 251.163 82.92C250.39 82.2267 249.397 81.88 248.183 81.88C247.157 81.88 246.257 82.1333 245.483 82.64C244.71 83.1333 244.11 83.8267 243.683 84.72C243.257 85.6 243.043 86.6267 243.043 87.8C243.043 88.9733 243.257 90.0067 243.683 90.9C244.11 91.78 244.71 92.4733 245.483 92.98C246.257 93.4733 247.15 93.72 248.163 93.72C249.403 93.72 250.417 93.3733 251.203 92.68C252.003 91.9867 252.477 91.0533 252.623 89.88H254.263C254.13 90.9467 253.783 91.88 253.223 92.68C252.677 93.48 251.97 94.1 251.103 94.54C250.25 94.98 249.277 95.2 248.183 95.2ZM256.788 95V84.8H258.288V87.4H258.388V95H256.788ZM258.388 89.68L258.127 87.26C258.368 86.3933 258.794 85.7333 259.408 85.28C260.021 84.8267 260.728 84.6 261.528 84.6C261.861 84.6 262.088 84.6267 262.208 84.68V86.22C262.141 86.1933 262.048 86.18 261.928 86.18C261.808 86.1667 261.661 86.16 261.488 86.16C260.474 86.16 259.701 86.4667 259.168 87.08C258.648 87.68 258.388 88.5467 258.388 89.68ZM268.528 95.2C267.528 95.2 266.648 94.9667 265.888 94.5C265.128 94.0333 264.534 93.4 264.108 92.6C263.694 91.7867 263.488 90.8733 263.488 89.86C263.488 88.8333 263.701 87.9267 264.128 87.14C264.554 86.3533 265.141 85.7333 265.888 85.28C266.648 84.8267 267.528 84.6 268.528 84.6C269.541 84.6 270.421 84.8267 271.168 85.28C271.928 85.7333 272.514 86.3533 272.928 87.14C273.354 87.9267 273.568 88.8333 273.568 89.86C273.568 90.8733 273.354 91.7867 272.928 92.6C272.514 93.4 271.928 94.0333 271.168 94.5C270.421 94.9667 269.541 95.2 268.528 95.2ZM268.528 93.84C269.221 93.84 269.821 93.6733 270.328 93.34C270.834 92.9933 271.228 92.52 271.508 91.92C271.788 91.32 271.928 90.6267 271.928 89.84C271.928 88.6667 271.614 87.7267 270.988 87.02C270.374 86.3133 269.554 85.96 268.528 85.96C267.514 85.96 266.694 86.3133 266.068 87.02C265.441 87.7267 265.128 88.6667 265.128 89.84C265.128 90.6267 265.268 91.32 265.548 91.92C265.828 92.52 266.221 92.9933 266.728 93.34C267.248 93.6733 267.848 93.84 268.528 93.84ZM279.427 95.2C278.174 95.2 277.174 94.9133 276.427 94.34C275.681 93.7533 275.254 92.9267 275.147 91.86H276.687C276.781 92.5133 277.067 93.0133 277.547 93.36C278.027 93.7067 278.667 93.88 279.467 93.88C280.174 93.88 280.727 93.7467 281.127 93.48C281.527 93.2133 281.727 92.8333 281.727 92.34C281.727 91.9933 281.614 91.6933 281.387 91.44C281.161 91.1733 280.707 90.9533 280.027 90.78L278.387 90.36C277.454 90.12 276.754 89.7533 276.287 89.26C275.821 88.7533 275.587 88.1533 275.587 87.46C275.587 86.58 275.914 85.8867 276.567 85.38C277.221 84.86 278.107 84.6 279.227 84.6C280.334 84.6 281.234 84.8667 281.927 85.4C282.621 85.9333 283.014 86.6733 283.107 87.62H281.567C281.474 87.0733 281.221 86.6533 280.807 86.36C280.394 86.0667 279.847 85.92 279.167 85.92C278.514 85.92 278.007 86.0467 277.647 86.3C277.287 86.54 277.107 86.8867 277.107 87.34C277.107 87.6867 277.234 87.9867 277.487 88.24C277.754 88.4933 278.194 88.7067 278.807 88.88L280.407 89.32C281.354 89.5733 282.067 89.96 282.547 90.48C283.027 91 283.267 91.6267 283.267 92.36C283.267 93.2533 282.927 93.9533 282.247 94.46C281.567 94.9533 280.627 95.2 279.427 95.2ZM289.076 95.2C287.822 95.2 286.822 94.9133 286.076 94.34C285.329 93.7533 284.902 92.9267 284.796 91.86H286.336C286.429 92.5133 286.716 93.0133 287.196 93.36C287.676 93.7067 288.316 93.88 289.116 93.88C289.822 93.88 290.376 93.7467 290.776 93.48C291.176 93.2133 291.376 92.8333 291.376 92.34C291.376 91.9933 291.262 91.6933 291.036 91.44C290.809 91.1733 290.356 90.9533 289.676 90.78L288.036 90.36C287.102 90.12 286.402 89.7533 285.936 89.26C285.469 88.7533 285.236 88.1533 285.236 87.46C285.236 86.58 285.562 85.8867 286.216 85.38C286.869 84.86 287.756 84.6 288.876 84.6C289.982 84.6 290.882 84.8667 291.576 85.4C292.269 85.9333 292.662 86.6733 292.756 87.62H291.216C291.122 87.0733 290.869 86.6533 290.456 86.36C290.042 86.0667 289.496 85.92 288.816 85.92C288.162 85.92 287.656 86.0467 287.296 86.3C286.936 86.54 286.756 86.8867 286.756 87.34C286.756 87.6867 286.882 87.9867 287.136 88.24C287.402 88.4933 287.842 88.7067 288.456 88.88L290.056 89.32C291.002 89.5733 291.716 89.96 292.196 90.48C292.676 91 292.916 91.6267 292.916 92.36C292.916 93.2533 292.576 93.9533 291.896 94.46C291.216 94.9533 290.276 95.2 289.076 95.2ZM300.109 95.18C299.149 95.18 298.342 94.9467 297.689 94.48C297.035 94.0133 296.589 93.3533 296.349 92.5L296.569 92.46V95H295.069V80.6H296.669V87.18L296.449 87.12C296.702 86.3333 297.162 85.72 297.829 85.28C298.509 84.8267 299.315 84.6 300.249 84.6C301.155 84.6 301.942 84.82 302.609 85.26C303.289 85.7 303.815 86.3133 304.189 87.1C304.562 87.8867 304.749 88.8067 304.749 89.86C304.749 90.9267 304.555 91.86 304.169 92.66C303.782 93.46 303.242 94.08 302.549 94.52C301.855 94.96 301.042 95.18 300.109 95.18ZM299.869 93.78C300.882 93.78 301.675 93.4333 302.249 92.74C302.822 92.0333 303.109 91.0733 303.109 89.86C303.109 88.66 302.822 87.72 302.249 87.04C301.675 86.3467 300.882 86 299.869 86C298.855 86 298.055 86.3533 297.469 87.06C296.882 87.7533 296.589 88.7067 296.589 89.92C296.589 91.1067 296.882 92.0467 297.469 92.74C298.055 93.4333 298.855 93.78 299.869 93.78ZM307.198 95V84.8H308.698V87.4H308.798V95H307.198ZM308.798 89.68L308.538 87.26C308.778 86.3933 309.204 85.7333 309.818 85.28C310.431 84.8267 311.138 84.6 311.938 84.6C312.271 84.6 312.498 84.6267 312.618 84.68V86.22C312.551 86.1933 312.458 86.18 312.338 86.18C312.218 86.1667 312.071 86.16 311.898 86.16C310.884 86.16 310.111 86.4667 309.578 87.08C309.058 87.68 308.798 88.5467 308.798 89.68ZM320.665 95C320.611 94.7733 320.571 94.5133 320.545 94.22C320.531 93.9267 320.525 93.5533 320.525 93.1H320.425V88.22C320.425 87.4467 320.245 86.88 319.885 86.52C319.538 86.1467 318.971 85.96 318.185 85.96C317.411 85.96 316.791 86.12 316.325 86.44C315.871 86.7467 315.618 87.1867 315.565 87.76H314.025C314.091 86.7733 314.505 86 315.265 85.44C316.025 84.88 317.018 84.6 318.245 84.6C319.511 84.6 320.451 84.9 321.065 85.5C321.678 86.1 321.985 87 321.985 88.2V93.1C321.985 93.3933 322.005 93.7 322.045 94.02C322.085 94.3267 322.138 94.6533 322.205 95H320.665ZM317.165 95.2C316.111 95.2 315.271 94.9467 314.645 94.44C314.031 93.92 313.725 93.2133 313.725 92.32C313.725 91.4267 314.031 90.7333 314.645 90.24C315.258 89.7467 316.151 89.4 317.325 89.2L320.885 88.6V89.92L317.565 90.44C316.818 90.56 316.251 90.76 315.865 91.04C315.478 91.32 315.285 91.7267 315.285 92.26C315.285 92.78 315.458 93.1733 315.805 93.44C316.165 93.7067 316.691 93.84 317.385 93.84C318.278 93.84 319.005 93.64 319.565 93.24C320.138 92.8267 320.425 92.2933 320.425 91.64L320.685 93.06C320.471 93.74 320.051 94.2667 319.425 94.64C318.811 95.0133 318.058 95.2 317.165 95.2ZM325.069 95V84.8H326.569V87.2H326.669V95H325.069ZM332.369 95V88.32C332.369 87.5467 332.175 86.9667 331.789 86.58C331.402 86.1933 330.829 86 330.069 86C329.402 86 328.809 86.1467 328.289 86.44C327.782 86.7333 327.382 87.14 327.089 87.66C326.809 88.18 326.669 88.7867 326.669 89.48L326.409 87.06C326.755 86.3 327.282 85.7 327.989 85.26C328.709 84.82 329.522 84.6 330.429 84.6C331.495 84.6 332.349 84.9067 332.989 85.52C333.642 86.12 333.969 86.9133 333.969 87.9V95H332.369ZM344.461 95V92.62L344.661 92.68C344.408 93.4533 343.941 94.0667 343.261 94.52C342.595 94.9733 341.795 95.2 340.861 95.2C339.941 95.2 339.135 94.98 338.441 94.54C337.761 94.1 337.228 93.4867 336.841 92.7C336.468 91.9133 336.281 90.9933 336.281 89.94C336.281 88.8733 336.475 87.94 336.861 87.14C337.248 86.34 337.788 85.72 338.481 85.28C339.175 84.84 339.988 84.62 340.921 84.62C341.881 84.62 342.688 84.8533 343.341 85.32C343.995 85.7867 344.441 86.4467 344.681 87.3L344.361 87.34V80.6H345.961V95H344.461ZM341.161 93.8C342.188 93.8 342.988 93.4533 343.561 92.76C344.148 92.0533 344.441 91.0933 344.441 89.88C344.441 88.6933 344.148 87.7533 343.561 87.06C342.975 86.3667 342.175 86.02 341.161 86.02C340.148 86.02 339.355 86.3667 338.781 87.06C338.208 87.7533 337.921 88.7133 337.921 89.94C337.921 91.14 338.208 92.0867 338.781 92.78C339.355 93.46 340.148 93.8 341.161 93.8Z" fill="#DFE3E8" />
              </svg>
            </Box>

            <Typography
              sx={{
                color: '#F5F5F5',
                fontFamily: 'Figtree, sans-serif',
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '140%',
                maxWidth: 480,
              }}
            >
              Cut wasted ad spend and drive incremental growth.
              Track every search signal and use it to inform what you do next across Google, Bing, Amazon, App Store, and more.
            </Typography>

          </Box>
        </Grid>

        <Grid
          xs={12}
          md={6}
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 3, sm: 4, md: 10 },
            py: { xs: 4, sm: 6, md: 0 },
          }}
        >

          <Box
            sx={{
              display: isLoadingSSO ? 'none' : 'block',
              width: '100%',
              maxWidth: { xs: '100%', sm: 420, md: 600 },
              border: '1px solid rgba(221, 231, 183, 0.30)',
              borderRadius: '12px',
              background: 'rgba(221, 231, 183, 0.06)',
              p: { xs: 3, sm: 4 },
            }}
          >

            <FormProvider methods={methods} onSubmit={methods.handleSubmit(signinWrapper)}>
              <Stack spacing={2} sx={{ mb: "32px" }}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: '30px',
                    fontWeight: 500,
                    lineHeight: '140%',
                    textAlign: 'left',
                  }}
                >
                  Login to your account
                </Typography>
              </Stack>
              <Stack spacing={2.5}>
                {!!errorMsg && <Alert sx={{ color: "#FF5630" }} severity="error">{errorMsg}</Alert>}
                <RHFTextField
                  name="email"
                  placeholder={t('auth.form.signin.Email')}
                  InputLabelProps={{
                    shrink: false,
                  }}
                  InputProps={{
                    sx: {
                      height: '52px',
                      padding: '0 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: '#FFFFFF',
                      backgroundColor: '#1D1E1A',
                      borderRadius: '8px',

                      '& input': {
                        padding: 0,
                        height: '100%',
                        color: '#FFFFFF',
                        fontFamily: 'Figtree, sans-serif',
                        fontSize: '14px',
                      },

                      '& input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px transparent inset',
                        WebkitTextFillColor: '#FFFFFF',
                        transition: 'background-color 9999s ease-out 0s',
                        background: 'transparent',
                      },
                    },
                  }}
                  sx={{
                    mb: "28px",
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />

                <Link
                  component={RouterLink}
                  href="/auth/forgot"
                  sx={{
                    alignSelf: 'flex-end',
                    color: '#ADADAD',
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '14%',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textUnderlineOffset: 'auto',
                    textUnderlinePosition: 'from-font',

                    '&:hover': {
                      color: '#FFFFFF',
                    },
                  }}
                >
                  {t('auth.form.signin.ForgotPassword?')}
                </Link>

                <RHFTextField
                  name="password"
                  type={password.value ? 'text' : 'password'}
                  placeholder={t('auth.form.signin.password')}
                  InputLabelProps={{
                    shrink: false,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={password.onToggle}
                          edge="end"
                          sx={{
                            color: '#FFFFFF',
                            padding: 0,
                            marginRight: '4px',
                          }}
                        >
                          <Iconify
                            icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                            width={20}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      height: '52px',
                      padding: '0 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: '#1D1E1A',
                      borderRadius: '8px',
                      color: '#FFFFFF',

                      '& input': {
                        padding: 0,
                        height: '100%',
                        color: '#FFFFFF',
                        fontFamily: 'Figtree, sans-serif',
                        fontSize: '14px',
                      },


                      '& input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px transparent inset',
                        WebkitTextFillColor: '#FFFFFF',
                        transition: 'background-color 9999s ease-out 0s',
                        background: 'transparent',
                      },
                    },
                  }}
                  sx={{
                    mb: "40px",
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused': {
                        outline: '1px solid rgba(255,255,255,0.12)',
                      },
                    },
                  }}
                />

                <LoadingButton
                  fullWidth
                  sx={{ backgroundColor: 'rgba(236, 255, 159, 1)', color: 'rgba(23, 25, 26, 1)' }}
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={methods.formState.isSubmitting}
                >
                  {t('auth.form.signin.Login')}
                </LoadingButton>

                <Divider
                  sx={{
                    '&::before, &::after': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: '#FFFFFF',
                      opacity: 0.6,
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: '15px',
                      fontWeight: 400,
                      lineHeight: '120%',
                      px: 2,
                    }}
                  >
                    or
                  </Typography>
                </Divider>

                <Stack display={'flex'} direction={{ xs: 'column', sm: 'row' }} gap={"24px"}>
                  <Button
                    onClick={() => signInWithProvider('google-oauth2')}
                    sx={{
                      width: '100%',
                      height: '44px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 24px 10px 12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(236, 255, 159, 0.10)',
                      border: 'none',
                      textTransform: 'none',

                      '&:hover': {
                        backgroundColor: 'rgba(236, 255, 159, 0.18)',
                      },
                    }}
                  >
                    <Iconify icon="logos:google-icon" width={20} />

                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontFamily: 'Figtree, sans-serif',
                        fontSize: '15px',
                        fontWeight: 400,
                        lineHeight: '120%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Sign in with Google
                    </Typography>
                  </Button>

                  <Button
                    onClick={() => signInWithSaml()}
                    sx={{
                      width: '100%',
                      height: '44px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 24px 10px 12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(236, 255, 159, 0.10)',
                      border: 'none',
                      textTransform: 'none',

                      '&:hover': {
                        backgroundColor: 'rgba(236, 255, 159, 0.18)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_enterprise)">
                          <path
                            d="M15.2666 14.4004C17.6598 14.4004 19.5996 16.3402 19.5996 18.7334V20.4668C19.5995 20.9453 19.212 21.333 18.7334 21.333C18.2548 21.333 17.8663 20.9454 17.8662 20.4668V18.7334C17.8662 17.2975 16.7025 16.1328 15.2666 16.1328H8.33301C6.89722 16.133 5.7334 17.2976 5.7334 18.7334V20.4668C5.7333 20.9454 5.3448 21.333 4.86621 21.333C4.38783 21.3328 4.0001 20.9452 4 20.4668V18.7334C4 16.3403 5.93992 14.4006 8.33301 14.4004H15.2666ZM11.7998 4C14.1929 4 16.1326 5.93992 16.1328 8.33301C16.1328 10.7262 14.193 12.667 11.7998 12.667C9.40664 12.6669 7.4668 10.7262 7.4668 8.33301C7.46697 5.93997 9.40674 4.00008 11.7998 4ZM11.7998 5.7334C10.364 5.73348 9.20037 6.89726 9.2002 8.33301C9.2002 9.7689 10.3639 10.9335 11.7998 10.9336C13.2357 10.9336 14.4004 9.76895 14.4004 8.33301C14.4002 6.89722 13.2356 5.7334 11.7998 5.7334Z"
                            fill="#ECF49B"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_enterprise">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </Box>

                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontFamily: 'Figtree, sans-serif',
                        fontSize: '15px',
                        fontWeight: 400,
                        lineHeight: '120%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Sign in with Enterprise
                    </Typography>
                  </Button>

                </Stack>

              </Stack>
            </FormProvider>
          </Box>
        </Grid>
      </Box>
    </>
  );
}